/**
 * Decision detail — /apps/decisions/:decisionId
 *
 * The Decisions app's view of one memorialized decision. The record itself
 * is DecisionRecordView, shared with the decision's canonical page at
 * /prioritization/decisions/:decisionId.
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDecisionsStore } from '@/stores/prioritization/decisionsStore';
import type { Decision } from '@/types/prioritization/decisions';
import { DecisionRecordView } from '@/components/prioritization/decisions/DecisionRecordView';
import { decisionSubtitle } from '@/components/prioritization/decisions/decisionRecordText';
import { getAppDefinition } from '../registry';
import { AppShell, ScreenHeading } from '../components/AppShell';
import { EmptyState, SecondaryButton } from '../components/primitives';

export default function DecisionDetailScreen() {
  const app = getAppDefinition('decisions')!;
  const { decisionId } = useParams<{ decisionId: string }>();
  const navigate = useNavigate();
  const { decisions, syncState, fetchDecisions } = useDecisionsStore();
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (decisions.length === 0) void fetchDecisions();
  }, [decisions.length, fetchDecisions]);

  const decision: Decision | undefined = useMemo(
    () => decisions.find((d) => d.id === decisionId),
    [decisions, decisionId],
  );

  const loading = syncState.isLoading && decisions.length === 0;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard unavailable — nothing to do */
    }
  };

  if (loading) {
    return (
      <AppShell app={app} backTo="/apps/decisions" backLabel="Back to the record">
        <p className="font-body text-sm text-gray-500 dark:text-[var(--text-sec)]">
          Loading the record…
        </p>
      </AppShell>
    );
  }

  if (!decision) {
    return (
      <AppShell app={app} backTo="/apps/decisions" backLabel="Back to the record">
        <EmptyState
          title="This decision isn't on this workspace's record"
          hint="The link may belong to another workspace, or the decision may have been removed."
          action={
            <SecondaryButton onClick={() => navigate('/apps/decisions')}>
              Back to the record
            </SecondaryButton>
          }
        />
      </AppShell>
    );
  }

  return (
    <AppShell
      app={app}
      backTo="/apps/decisions"
      backLabel="Back to the record"
      actions={
        <SecondaryButton onClick={copyLink} className="!px-3 !py-1.5 text-xs">
          {copied ? 'Copied' : 'Copy link'}
        </SecondaryButton>
      }
    >
      <ScreenHeading title={decision.title} subtitle={decisionSubtitle(decision)} />
      <DecisionRecordView
        decision={decision}
        decisions={decisions}
        decisionHref={(id) => `/apps/decisions/${id}`}
      />
    </AppShell>
  );
}
